import "reactflow/dist/style.css";

import { useQuery } from "@tanstack/react-query";
import { Editor, JSONContent } from "@tiptap/react";
import classNames from "classnames";
import { nanoid } from "nanoid";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactFlow, { Background, Controls, Edge, MiniMap, Node, useReactFlow } from "reactflow";
import * as Y from "yjs";

import { useActiveEditor, useQueries } from "@/hooks";
import { getPage, getPageIds } from "@/hooks/useQueries/pages";

import styles from "./Graph.module.scss";
import GraphEdge from "./GraphEdge";
import GraphNodeReadOnly from "./GraphNodeReadOnly";
import useEdgesStateSynced from "./useEdgesStateSynced";
import useNodesStateSynced from "./useNodesStateSynced";
import { getEditorNodes } from "./utils";

const nodeTypes = {
  GraphNode: GraphNodeReadOnly,
  SyncedNode: GraphNodeReadOnly,
};
const edgeTypes = { default: GraphEdge };

interface GraphProps extends React.SelectHTMLAttributes<HTMLDivElement> {
  pageId: string | undefined;
  editor: Editor;
  ydoc: Y.Doc;
  isClosable?: boolean;
  active: boolean;
}

export default forwardRef<HTMLDivElement, GraphProps>(
  ({ pageId, editor, className, ydoc, active, isClosable = false, ...props }, ref) => {
    const { setActiveGraph } = useActiveEditor();
    const reactFlowInstance = useReactFlow();
    const navigate = useNavigate();
    let { projectPublicId } = useParams();
    const { currentProject } = useQueries();

    const { data: page } = useQuery({
      queryKey: ["pages", currentProject?.id, pageId],
      queryFn: ({ signal }) => getPage(signal, currentProject?.id, pageId),
      enabled: Boolean(currentProject?.id && pageId),
      retry: false,
    });

    const { data: pageIds } = useQuery({
      queryKey: ["page-ids"],
      queryFn: ({ signal }) => getPageIds(signal, currentProject?.id),
      enabled: Boolean(currentProject?.id),
      initialData: [],
    });

    useEffect(() => {
      if (active && reactFlowInstance) {
        setActiveGraph(reactFlowInstance);
      }
    }, [active, reactFlowInstance]);

    const [pageContent, setPageContent] = useState<JSONContent>();
    const [nodeIds, setNodeIds] = useState<string[]>([]);
    const edgesMap = useRef<Y.Map<Edge>>();
    const nodesMap = useRef<Y.Map<Node>>();
    const [nodes, onNodesChange] = useNodesStateSynced(nodeIds, nodesMap.current, edgesMap.current);
    const [edges] = useEdgesStateSynced(edgesMap.current);

    useEffect(() => {
      if (pageId) {
        nodesMap.current = ydoc.getMap<Node>("nodes");
        edgesMap.current = ydoc.getMap<Edge>("edges");
      }
    }, [pageId, ydoc, nodesMap]);

    const [initialResize, setInitialResize] = useState<boolean>(false);

    useEffect(() => {
      if (nodes.length && !initialResize) {
        setTimeout(() => {
          reactFlowInstance.fitView();
          setInitialResize(true);
        }, 10);
      }
    }, [nodes]);

    useEffect(() => {
      const update = ({ editor }: { editor: Editor }) => {
        setPageContent(editor?.getJSON());
      };
      setPageContent(editor?.getJSON());
      // @ts-ignore
      editor?.on("update", update);
      return () => {
        // @ts-ignore
        editor?.off("update", update);
      };
    }, [editor]);

    useEffect(() => {
      if (!pageContent || !pageContent?.content || !nodesMap.current) return;

      const handleDebounce = setTimeout(() => {
        const { ids } = getEditorNodes(editor);
        setNodeIds(ids);
      }, 250);
      return () => clearTimeout(handleDebounce);
    }, [pageContent]);

    const onNodeDoubleClick = (event: React.MouseEvent, node: Node) => {
      if (pageIds.includes(node?.id)) {
        navigate(`/public/${projectPublicId}/${node?.id}`);
      }
    };

    if (!page?.content) {
      return (
        <div {...props} className={classNames(className, styles.border)}>
          <div className={styles.loader}>Loading...</div>
        </div>
      );
    }

    return (
      <div {...props} className={classNames(className, styles.border, styles.readOnly)}>
        <ReactFlow
          key={`${nodes}${edges}`}
          nodes={nodes}
          edges={edges}
          minZoom={0.1}
          onNodeDoubleClick={onNodeDoubleClick}
          onNodesChange={onNodesChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
        >
          <Controls />
          <MiniMap />
          <Background gap={12} size={1} />
        </ReactFlow>
      </div>
    );
  }
);
